import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useUpdatePersonMutation } from "../../../../../features/peopleApi";
import { roleDepartment } from "../../../../lib/atecos/dto";
import { addBannerWithTimeout } from "../../../../lib/centralizedBanner/slice";
import { keycloak } from "../../../../lib/keycloak";
import { useAppDispatch } from "../../../../lib/redux/hooks";
import { groupBy } from "../../../../lib/utils";
import { LinkedinIcon } from "../../../../ui/icons/linkedin";
import Button from "../../../../ui/molecules/button";
import Input from "../../../../ui/molecules/input";
import { SelectCustom } from "../../../../ui/molecules/select";
import { PersonDTO } from "../dto";
import { PrivacyComponent } from "../privacy";
import { PersonEmails } from "./personEmails";
import { UpdatePersonFormValues, updatePersonSchema } from "./validation";

interface Props {
    isInTargetList: boolean
    handleClose: () => void
    person?: PersonDTO
    setIsEmpty?: (isEmpty: boolean) => void
}

export function EditPersonForm(props: Props) {
    const { isInTargetList, handleClose, person, setIsEmpty } = props
    const { contactId } = useParams();

    const form = useForm<UpdatePersonFormValues>({
        resolver: zodResolver(updatePersonSchema),
        defaultValues: {
            name: "",
            surname: "",
            phone: "",
            emails: [],
            linkedinProfile: "",
            role: "",
            department: "",
        },
    });

    useEffect(() => {
        if (contactId === undefined) {
            form.reset({});
            form.clearErrors();
        }
    }, [contactId])

    useEffect(() => {
        if (person) {
            const formValues: Partial<UpdatePersonFormValues> = {
                name: person.name,
                surname: person.surname,
                phone: person.phone,
                emails: person.emails,
                linkedinProfile: person.linkedinProfile,
                role: person.role,
                department: person.department,
            };
            form.reset(formValues);
            form.clearErrors();
        }
    }, [person, form]);

    useEffect(() => {
        setIsEmpty && setIsEmpty(form.formState.isDirty);
    }, [form.formState.isDirty]);

    const [updatePerson] = useUpdatePersonMutation();
    const dispatch = useAppDispatch();

    const handleSubmit = async () => {
        try {
            await updatePerson({
                id: person?.id || "",
                data: form.getValues(),
            }).unwrap();
            setIsEmpty && setIsEmpty(false);
            dispatch(addBannerWithTimeout({
                message: "Contatto aggiornato con successo",
                variant: "success"
            }));
        } catch (error) {
            dispatch(addBannerWithTimeout({
                message: "Errore nell'aggiornamento del contatto",
                variant: "error"
            }));
        }
    };

    const toValue = (v: any) => {
        if (v == null) return undefined;
        if (Array.isArray(v)) return v.map((it) => (it?.value ?? it));
        return v.value ?? v;
    };

    const role = form.watch("role")

    useEffect(() => {
        const department = roleDepartment.find(
            (_role) => _role.role === role
        )?.department || ""

        form.setValue("department", department, { shouldValidate: true })
    }, [role, form])

    return (
        <div key={0} className="px-6 py-4 flex flex-col gap-4">
            <div className="flex gap-2">
                <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                        <Input
                            {...field}
                            defaultValue={field.value || ""}
                            placeholder="Nome"
                            label="Nome"
                            supportingText={form.formState.errors.name?.message as string | undefined}
                            error={!!form.formState.errors.name}
                        />
                    )}
                />
                <Controller
                    name="surname"
                    control={form.control}
                    render={({ field }) => (
                        <Input
                            {...field}
                            defaultValue={field.value || ""}
                            placeholder="Cognome"
                            label="Cognome"
                            supportingText={form.formState.errors.surname?.message as string | undefined}
                            error={!!form.formState.errors.surname}
                        />
                    )}
                />
            </div>
            <div className="flex flex-col gap-[6px]">
                <Controller
                    name="role"
                    control={form.control}
                    render={({ field }) => (
                        <SelectCustom
                            isClearable
                            label="Ruolo"
                            menuPortalTargetIsNotBody
                            defaultValue={field.value || undefined}
                            onChange={(v) => field.onChange(toValue(v))}
                            error={!!form.formState.errors.role}
                            errorLabel={String(form.formState.errors.role?.message ?? "")}
                            noOptionsMessage="Non sono presenti ruoli."

                            placeholder={"Cerca per ruolo"}
                            options={groupBy(roleDepartment, "role").map((sector) => ({
                                value: sector.key,
                                label: sector.key,
                            }))}
                        />
                    )}
                />
            </div>
            <Controller
                name="department"
                control={form.control}
                render={({ field }) => (
                    <Input
                        label="Reparto"
                        disabled
                        {...field}
                        value={
                            roleDepartment.find(
                                (_role) => _role.role === role
                            )?.department || ""
                        }
                        placeholder="Il reparto verrà impostato in base al ruolo"
                        supportingText={form.formState.errors.department?.message as string | undefined}
                        error={!!form.formState.errors.department}
                    />
                )}
            />
            <div className="flex items-start gap-2">
                <Controller
                    name="phone"
                    control={form.control}
                    render={({ field }) => (
                        <Input
                            {...field}
                            value={field.value || ""}
                            placeholder="Smartphone"
                            label="Smartphone"
                            supportingText={form.formState.errors.phone?.message as string | undefined}
                            error={!!form.formState.errors.phone}
                        />
                    )}
                />
                <div className="pt-5">
                    <PrivacyComponent person={person} personId={person?.id!} privacy={person?.privacy} section="form" />
                </div>
            </div>
            <PersonEmails
                isInTargetList={isInTargetList}
                form={form}
            />
            <Controller
                name="linkedinProfile"
                control={form.control}
                render={({ field }) => (
                    <Input
                        {...field}
                        label="LinkedIn"
                        startIcon={<LinkedinIcon color={""} size={0} />}
                        value={field.value || ""}
                        placeholder="Profilo LinkedIn"
                        supportingText={form.formState.errors.linkedinProfile?.message as string | undefined}
                        error={!!form.formState.errors.linkedinProfile}
                    />
                )}
            />
            <div className={clsx("py-6 justify-end items-center gap-2 flex")}>
                <Button
                    size={"sm"}
                    iconPosition={"off"}
                    variant={"outline"}
                    color={"blue"}
                    label="Annulla"
                    onClick={() => handleClose()}
                />
                <Button
                    size={"sm"}
                    iconPosition={"off"}
                    variant={"solid"}
                    disabled={keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')}
                    color={"blue"}
                    label="Modifica contatto"
                    onClick={() => form.handleSubmit(handleSubmit)()}
                />
            </div>
        </div>
    )

}